/**
 * Centralized error handling utilities.
 */

import { notifications } from '@mantine/notifications';
import { AxiosError } from 'axios';
import { logger } from './logger';

/**
 * API error response structure from backend.
 */
interface ApiErrorResponse {
  detail?: string;
  message?: string;
}

/**
 * Default error messages by HTTP status code.
 */
const DEFAULT_ERRORS: Record<number, string> = {
  400: 'Requisição inválida',
  401: 'Sessão expirada. Por favor, faça login novamente.',
  403: 'Você não tem permissão para esta ação',
  404: 'Recurso não encontrado',
  422: 'Dados inválidos',
  429: 'Muitas tentativas. Aguarde um momento.',
  500: 'Erro interno do servidor',
  502: 'Servidor indisponível',
  503: 'Serviço temporariamente indisponível',
};

/**
 * Extract error message from various error types.
 *
 * @param error - The error object
 * @param fallback - Fallback message if no specific message found
 * @returns Human-readable error message
 */
export function getErrorMessage(error: unknown, fallback = 'Ocorreu um erro inesperado'): string {
  // Axios error
  if (error instanceof AxiosError) {
    const response = error.response;

    if (response) {
      // Try to get message from response data
      const data = response.data as ApiErrorResponse | undefined;
      if (data?.detail) return data.detail;
      if (data?.message) return data.message;

      // Use default message for status code
      if (DEFAULT_ERRORS[response.status]) {
        return DEFAULT_ERRORS[response.status];
      }
    }

    // Network error
    if (error.code === 'ERR_NETWORK') {
      return 'Erro de conexão. Verifique sua internet.';
    }

    // Timeout
    if (error.code === 'ECONNABORTED') {
      return 'A requisição demorou muito. Tente novamente.';
    }
  }

  // Standard Error object
  if (error instanceof Error) {
    return error.message || fallback;
  }

  // String error
  if (typeof error === 'string') {
    return error;
  }

  return fallback;
}

/**
 * Show error notification.
 *
 * @param error - The error object
 * @param title - Notification title (default: 'Erro')
 */
export function showError(error: unknown, title = 'Erro'): void {
  const message = getErrorMessage(error);

  logger.error(title, error);

  notifications.show({
    title,
    message,
    color: 'red',
    autoClose: 5000,
  });
}

/**
 * Show success notification.
 *
 * @param message - Success message
 * @param title - Notification title (default: 'Sucesso')
 */
export function showSuccess(message: string, title = 'Sucesso'): void {
  notifications.show({
    title,
    message,
    color: 'green',
    autoClose: 3000,
  });
}

/**
 * Show warning notification.
 *
 * @param message - Warning message
 * @param title - Notification title (default: 'Atenção')
 */
export function showWarning(message: string, title = 'Atenção'): void {
  notifications.show({
    title,
    message,
    color: 'yellow',
    autoClose: 4000,
  });
}

/**
 * Show info notification.
 *
 * @param message - Info message
 * @param title - Optional title
 */
export function showInfo(message: string, title?: string): void {
  notifications.show({
    title,
    message,
    color: 'blue',
    autoClose: 3000,
  });
}

/**
 * Handle async operation with error handling.
 *
 * @param operation - Async operation to execute
 * @param errorTitle - Title for error notification
 * @returns Result of operation or undefined on error
 */
export async function handleAsync<T>(
  operation: () => Promise<T>,
  errorTitle = 'Erro'
): Promise<T | undefined> {
  try {
    return await operation();
  } catch (error) {
    showError(error, errorTitle);
    return undefined;
  }
}

/**
 * Check if error is an authentication error (401).
 */
export function isAuthError(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 401;
}

/**
 * Check if error is a permission error (403).
 */
export function isPermissionError(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 403;
}

/**
 * Check if error is a network error.
 */
export function isNetworkError(error: unknown): boolean {
  return error instanceof AxiosError && error.code === 'ERR_NETWORK';
}
